import { Link } from "react-router-dom";
import { Check, ArrowRight, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { SERVICES, URGENCE, MARQUEE_ITEMS } from "@/lib/data";

const ServiceBlock = ({ service, flip }) => (
  <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${flip ? "" : ""}`}>
    <Reveal className={flip ? "lg:order-2" : ""}>
      <div className="group relative overflow-hidden rounded-3xl">
        <img
          src={service.image}
          alt={`${service.title} — ${service.tag}`}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute bottom-5 left-5 rounded-full bg-roch-orange px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
          {service.tag}
        </span>
      </div>
    </Reveal>
    <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
      <p className="font-display text-6xl font-extrabold text-roch-orange/25">{service.num}</p>
      <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-roch-navy sm:text-3xl lg:text-4xl">
        {service.title}
      </h2>
      <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">{service.desc}</p>
      <ul className="mt-7 grid gap-3 sm:grid-cols-2">
        {service.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-roch-orange/15">
              <Check className="h-3 w-3 text-roch-orange" />
            </span>
            {f}
          </li>
        ))}
      </ul>
      <Link
        to="/devis"
        data-testid={`services-devis-link-${service.id}`}
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-roch-navy px-6 py-3 font-display text-sm font-bold text-white transition-colors duration-300 hover:bg-roch-orange hover:text-roch-navy"
      >
        Chiffrer ce projet
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </Reveal>
  </div>
);

export default function Services() {
  return (
    <main data-testid="services-page">
      <Seo
        title="Nos services — LE ROCH"
        siteName="LE ROCH"
        description="Couvreur à Pontivy (Morbihan) : couverture ardoise et zinc, étanchéité de toitures terrasses et cintrées, isolation, bardage et zinguerie — neuf et rénovation."
      />
      <PageHero
        kicker="Nos services"
        lines={["QUATRE MÉTIERS,", "UNE EXIGENCE."]}
        accentIndex={1}
        description="12 compagnons couvreurs et 2 chargés de projet au service des particuliers, architectes, promoteurs, syndics et collectivités — partout dans le Morbihan et les Côtes d'Armor."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto flex max-w-7xl flex-col gap-24 px-4 sm:px-6 lg:gap-32 lg:px-8">
          {SERVICES.map((s, i) => (
            <ServiceBlock key={s.id} service={s} flip={i % 2 === 1} />
          ))}

          {/* Urgence */}
          <Reveal>
            <div className="grain relative overflow-hidden rounded-3xl bg-roch-navy p-8 sm:p-12 lg:p-16" data-testid="services-urgence-panel">
              <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-roch-orange/20 blur-[90px]" />
              <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center">
                <div>
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-roch-orange">
                    <Wrench className="h-4 w-4" /> {URGENCE.num} — {URGENCE.tag}
                  </p>
                  <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
                    {URGENCE.title}
                  </h2>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-white/60">{URGENCE.desc}</p>
                  <Link
                    to="/devis"
                    data-testid="services-urgence-contact-button"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-roch-orange px-6 py-3 font-display text-sm font-bold text-roch-navy transition-colors duration-300 hover:bg-roch-orangeHover"
                  >
                    Demander une intervention
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {URGENCE.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm font-medium text-white/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-roch-orange" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} dark />
      <CtaBand />
    </main>
  );
}
